import { AfterViewInit, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
    ApprovedPCHDurationModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    CriticalityModel,
    DateUtilities,
    EventService,
    LinkService,
    ModalClass,
    NotificationDurationModel,
    NotificationModel,
    NotificationService,
    NotificationUtilities,
    SynchronizationService,
    ValueHelperService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faBan, faFlag, faInfoCircle, faNewspaper, faPhone, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of, Subscription } from 'rxjs';
import { catchError, mergeMap, take } from 'rxjs/operators';
import { ContractComponent } from '../contract/contract.component';
import { CustomerComponent } from '../customer/customer.component';
import { InternalInfoComponent } from '../internal-info/internal-info.component';
import { ObjectInfoComponent } from '../object-info/object-info.component';
import { CriticalityComponent } from '../criticality/criticality.component';
import { DurationComponent } from '../duration/duration.component';
import { PCHDurationComponent } from '../pch-duration/pch-duration.component';

@Component({
    selector: 'pace-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input()
    public notification: NotificationModel;

    public notificationTypeString = '';
    public descText = '';
    public tidorFLNumber = '';
    public contractType = '';
    public customerPurchOrder = '';
    public modality = '';
    public criticality = '';
    public readonly faPencil = faPencilAlt;
    public equipmentNumber = '';
    public isMultiDayNotification: boolean;
    public displayTechnicalIdNumber: boolean;
    public displayFunctionLocation: boolean;
    private notificationsUpdated: Subscription;

    public displayModality: boolean;
    public displayCriticality: boolean;
    public displayDurationChange: boolean;
    public displayEquipment: boolean;
    public displayContract: boolean;
    public displayClarifyText: boolean;
    public displayInternalInfo: boolean;
    public displayANALink: boolean;
    public displayApprovedPCH = false;
    public approvedPCHDurationModel: ApprovedPCHDurationModel;

    public faBan = faBan;
    public faInfoCircle = faInfoCircle;
    public faNewspaper = faNewspaper;
    public faFlag = faFlag;

    public readonly faPhone = faPhone;
    public availableInternalInfoCounts: number;
    public strings: {
        startDateTime: string;
        endDateTime: string;
        weekDay: string;
    };
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly notificationUtilities: NotificationUtilities,
        private readonly notificationService: NotificationService,
        private readonly eventService: EventService,
        private readonly valueHelperService: ValueHelperService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly synchronizationService: SynchronizationService,
        private readonly linkService: LinkService,
        private readonly configService: ConfigService,
    ) {
        this.strings = { startDateTime: '', endDateTime: '', weekDay: '' };
        this.displayTechnicalIdNumber = this.clientManager.isNgmP58();
        this.displayFunctionLocation = this.clientManager.isNgmAP0() || this.clientManager.isPaceUS();

        this.displayModality = this.clientManager.isPaceUS();
        this.displayEquipment = this.clientManager.isPaceUS();
        this.displayContract = !this.clientManager.isPaceUS();
        this.displayClarifyText = this.clientManager.isPaceUS();
        this.displayInternalInfo = this.clientManager.isNgmP58();
        this.displayANALink = this.clientManager.isPaceUS() && this.configService.getAnaLink().length > 0;
        this.displayCriticality = this.configService.getCriticalityChangeEnabled();
        this.displayDurationChange = this.configService.getEnableDurationChange();
    }

    public ngOnInit(): void {
        this.displayApprovedPCH =
            this.configService.getCentricarePCHEnabled() && this.notification.isCentriCareContract();
        if (this.displayApprovedPCH) {
            this.approvedPCHDurationModel = new ApprovedPCHDurationModel(this.notification);
        }
    }

    public ngAfterViewInit(): void {
        this.notificationsUpdated = this.eventService.notificationsUpdated$.subscribe(() => {
            if (!this.notification) {
                return;
            }
            // get the latest notification model with updated properties
            const notification = this.notificationService.getNotificationByIdentifier(this.notification.identifier);

            if (!notification) {
                throw new Error(`Could not re-load notification for identifier ${this.notification.identifier}`);
            }

            this.notification = notification;

            this.initData();
        });
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj.notification && changesObj.notification.currentValue) {
            this.notification = <NotificationModel>changesObj.notification.currentValue;
            this.initData();
        }
    }

    public ngOnDestroy(): void {
        if (typeof this.notificationsUpdated !== 'undefined') {
            this.notificationsUpdated.unsubscribe();
        }
    }

    public initData(): void {
        const mainEquipment = this.notification.getMainEquipment();
        if (!mainEquipment) {
            this.log.error(
                'HeaderComponent: initData: No Main Equipment available for notification ' +
                    this.notification.identifier.toString(),
            );
        }

        this.notificationTypeString = this.notificationUtilities.getNotificationTypeDescription(this.notification);
        this.descText = mainEquipment ? mainEquipment.equiShortDesc.value : '';

        if (this.displayCriticality) {
            this.criticality = this.notificationUtilities.getNotificationStatusInArrivalDescription(this.notification);
        }

        // For PACE US the functional location is being saved in the tidnr.
        if (this.displayTechnicalIdNumber || this.clientManager.isPaceUS()) {
            this.tidorFLNumber = mainEquipment ? mainEquipment.tidnr.value : '';
        } else {
            this.tidorFLNumber = this.notification.functionalLoc;
        }
        this.contractType = mainEquipment ? mainEquipment.contractType.value : '';
        this.customerPurchOrder = this.notification.customerPurchaseOrderNr;
        this.isMultiDayNotification = this.notificationService.isMultiDayNotification(this.notification);

        if (mainEquipment) {
            const modalityValue = this.valueHelperService.getModalityValue(mainEquipment.division.value);

            this.modality = modalityValue ? modalityValue.descText : '';
            this.equipmentNumber = mainEquipment.equnr.value;
        }
        if (this.displayInternalInfo) {
            this.updateInternalInfoCounts();
        }

        this.strings = {
            startDateTime: this.getStartDate(this.notification),
            endDateTime: this.getEndDate(this.notification),
            weekDay: this.getWeekDay(this.notificationService.getNotificationStart(this.notification)),
        };
    }

    /**
     * Categories like parking, sub-system, dispatch are multiline text model, check if these categories contains an information
     * whereas, contact category contains dropdown of contacts, If contact category has at least one contact then we should count it as 1.
     *
     * @private
     */
    private updateInternalInfoCounts(): void {
        const availableContactCounts =
            this.notification.internalInfoModel.contactModel.value.filter(
                (entry) => entry.model.formValidationStatus === ConfigConstants.formValid,
            ).length > 0
                ? 1
                : 0;
        const availableCategoryCounts = this.notification.internalInfoModel.categoryModel
            .getAllCategoriesModel()
            .filter((category) => category.textLine.value.length > 0).length;

        this.availableInternalInfoCounts = availableCategoryCounts + availableContactCounts;
    }

    public openContractModal(): void {
        this.modalService.show(ContractComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                notification: this.notification,
            },
            ariaLabelledBy: 'contract-modal-title',
        });
    }

    public openInternalInfoModal(): void {
        const modalInstance = this.modalService.show(InternalInfoComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: this.notification.internalInfoModel,
            },
            ariaLabelledBy: 'internal-info-modal-title',
        });
        modalInstance.content?.modalResult
            .pipe(
                take(1),
                mergeMap((result) => {
                    if (result) {
                        this.updateInternalInfoCounts();
                        return this.synchronizationService.sendOfflineData();
                    }
                    return of(undefined);
                }),
                catchError((error) => {
                    this.log.error(
                        `InternalInfoComponent: submitInternalInfoModel$: Error while saving the Internal Info contact model:`,
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    public openObjectInfoModal(): void {
        this.modalService.show(ObjectInfoComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                notification: this.notification,
            },
            ariaLabelledBy: 'object-info-modal-title',
        });
    }

    public openCustomerModal(): void {
        this.modalService.show(CustomerComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                notification: this.notification,
            },
            ariaLabelledBy: 'customer-modal-title',
        });
    }

    public openCriticalityModal(): void {
        this.modalService.show(CriticalityComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new CriticalityModel(this.notification),
            },
            ariaLabelledBy: 'criticality-modal-title',
        });
    }

    public openDurationModal(): void {
        this.modalService.show(DurationComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new NotificationDurationModel(this.notification),
            },
            ariaLabelledBy: 'duration-modal-title',
        });
    }

    public openPCHDurationModal(): void {
        this.modalService.show(PCHDurationComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: this.approvedPCHDurationModel,
            },
            ariaLabelledBy: 'pch-duration-modal-title',
        });
    }

    public displayCustomerPONumber(): boolean {
        return this.clientManager.isNgmP58() && this.customerPurchOrder !== '';
    }

    /** Only used in PACE US MARS */
    public openANALink(): void {
        const mainEquipment = this.notification.getMainEquipment();
        const link = `${this.configService.getAnaLink()}${mainEquipment?.tidnr.value}`;

        this.linkService.openLink(link);
    }

    private getStartDate(notification: NotificationModel): string {
        const startDate = this.notificationService.getNotificationStart(notification);
        return DateUtilities.formatDate(startDate) + ' | ' + DateUtilities.formatTime(startDate);
    }

    private getEndDate(notification: NotificationModel): string {
        const endDate = this.notificationService.getNotificationEnd(notification);
        if (this.isMultiDayNotification) {
            return (
                this.getWeekDay(endDate) +
                ' ' +
                DateUtilities.formatDate(endDate) +
                ' | ' +
                DateUtilities.formatTime(endDate)
            );
        }
        return ' - ' + DateUtilities.formatTime(endDate);
    }

    private getWeekDay(date: moment.Moment): string {
        return DateUtilities.formatWeekday(date);
    }
}
